import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";

export interface NoteAuthor {
  id: number;
  username: string;
  display_name: string;
}

export interface Note {
  id: number;
  author_id: number;
  author?: NoteAuthor;
  title: string;
  body: string;
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface NoteListResponse {
  items: Note[];
}

export interface NoteListParams {
  q?: string;
}

export function useNoteList(params: NoteListParams = {}) {
  const u = new URLSearchParams();
  if (params.q) u.set("q", params.q);
  const s = u.toString();
  return useQuery<{ notes: Note[] } & NoteListResponse>({
    queryKey: ["notes", params],
    queryFn: async () => {
      const r = await apiGet<NoteListResponse>("/notes" + (s ? "?" + s : ""));
      // Provide both names for convenience.
      return { ...r, notes: r.items };
    },
  });
}

export interface NoteInput {
  title: string;
  body?: string;
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NoteInput) => apiPost<Note>("/notes", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<NoteInput> & { id: number }) =>
      apiPut<Note>(`/notes/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete<void>(`/notes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
  });
}
